import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Heart, Users, Star, ArrowRight, Phone, Mail, Compass, Leaf } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import apiService from '../services/api.service';

const Home = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await apiService.services.getAll();
        // Ensure we always set an array, even if API returns unexpected data
        setServices(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching services:', error);
        // Set empty array on error to prevent crashes
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const testimonials = [];

  const features = [
    {
      icon: <Sparkles className="w-8 h-8 text-spiritual" />,
      title: "Energy Alignment",
      description: "Maintaining high vibrational practices that uplift, cleanse and elevate every seeker."
    },
    {
      icon: <Heart className="w-8 h-8 text-spiritual" />,
      title: "Guided Meditation",
      description: "Rediscovering your natural state of balance and wellbeing through mindful practices."
    },
    {
      icon: <Users className="w-8 h-8 text-spiritual" />,
      title: "Chakra Work",
      description: "Harmonizing and balancing your energy centers for optimal spiritual alignment."
    },
    {
      icon: <Compass className="w-8 h-8 text-spiritual" />,
      title: "Spiritual Cleansing",
      description: "Purifying and clearing energetic blockages to restore clarity and inner peace."
    },
    {
      icon: <Star className="w-8 h-8 text-spiritual" />,
      title: "Aura Scanning",
      description: "Intuitive assessment of your energy field to identify areas needing healing attention."
    },
    {
      icon: <Leaf className="w-8 h-8 text-spiritual" />,
      title: "Breathwork & Energy Therapy",
      description: "Holistic healing techniques combining conscious breathing with energy-based therapies."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: 'linear-gradient(135deg, hsl(var(--spiritual) / 0.9) 0%, hsl(var(--compassion) / 0.82) 35%, hsl(var(--sacred) / 0.55) 100%)'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/20" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="fade-in">
            <h1 className="hero-title font-heading font-bold text-5xl md:text-7xl text-white mb-6 leading-tight">
              Awaken Your 
              <span className="bg-gradient-to-r from-compassionScale-100 to-sacredScale-100 bg-clip-text text-transparent">
                Inner Light
              </span>
            </h1>
            
            <p className="hero-subtitle text-xl md:text-2xl text-compassionScale-100 mb-8 max-w-2xl mx-auto font-light leading-relaxed">
              Discover the transformative power of energy healing and meditation at Mystic Prana. 
              Your journey to balance and spiritual wellness begins here.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center slide-up">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-white text-spiritual hover:bg-sacredScale-50 px-8 py-4 text-lg font-semibold rounded-full shadow-2xl hover:shadow-sacredScale-200/40 hover:scale-105 transition-all duration-300"
                  data-testid="get-started-btn"
                >
                  Begin Your Journey
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              
              <Link to="/about">
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="text-white border-white hover:bg-white hover:text-spiritual px-8 py-4 text-lg font-semibold rounded-full transition-all duration-300 hover:scale-105"
                  data-testid="learn-more-btn"
                >
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/60 rounded-full mt-2 animate-pulse" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-gradient-to-b from-compassionScale-50 to-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title font-heading font-bold text-4xl md:text-5xl text-spiritual mb-6">
              Healing Modalities
            </h2>
            <p className="text-xl text-compassion-foreground max-w-3xl mx-auto leading-relaxed">
              Mystic Prana brings together a diverse range of healing modalities, 
              blending ancient wisdom with modern practices to support your complete transformation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="card group hover:scale-105 transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm"
                data-testid={`feature-${index}`}
              >
                <CardContent className="p-8 text-center">
                  <div className="mb-6 flex justify-center">
                    <div className="w-16 h-16 bg-compassionScale-100 rounded-full flex items-center justify-center group-hover:bg-compassionScale-200 transition-colors duration-300">
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className="font-heading font-semibold text-2xl text-spiritual mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-compassion-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title font-heading font-bold text-4xl md:text-5xl text-spiritual mb-6">
              Our Services
            </h2>
            <p className="text-xl text-compassion-foreground max-w-3xl mx-auto leading-relaxed">
              Personalized healing experiences tailored to your unique spiritual journey and wellness goals.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="loading-spinner" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <Card 
                  key={service.id} 
                  className="card group hover:scale-102 transition-all duration-300"
                  data-testid={`service-${index}`}
                >
                  <CardContent className="p-8">
                    <div className="mb-4">
                      <h3 className="font-heading font-semibold text-2xl text-spiritual group-hover:text-spiritual/80 transition-colors">
                        {service.name}
                      </h3>
                    </div>
                    
                    <p className="text-compassion-foreground mb-4 leading-relaxed">
                      {service.description}
                    </p>
                    
                    <div className="mb-6">
                      <span className="text-sm text-compassion-foreground font-medium">
                        Duration: {service.duration}
                      </span>
                    </div>
                    
                    <div className="mb-6">
                      <h4 className="font-semibold text-spiritual mb-2">Benefits:</h4>
                      <div className="flex flex-wrap gap-2">
                        {(() => {
                          // Handle benefits - could be string or array
                          const benefitsList = Array.isArray(service.benefits) 
                            ? service.benefits 
                            : typeof service.benefits === 'string' 
                              ? service.benefits.split(',').map(b => b.trim()).filter(b => b)
                              : [];
                          
                          return benefitsList.length > 0 ? (
                            benefitsList.map((benefit, benefitIndex) => (
                              <span 
                                key={benefitIndex}
                                className="px-3 py-1 bg-compassionScale-100 text-spiritual rounded-full text-sm font-medium"
                              >
                                {benefit}
                              </span>
                            ))
                          ) : (
                            <p className="text-compassion-foreground text-sm">{service.benefits || 'Various benefits available'}</p>
                          );
                        })()}
                      </div>
                    </div>
                    
                    <Link to="/contact">
                      <Button 
                        className="w-full bg-spiritual hover:bg-spiritual/90 text-white font-semibold py-3 rounded-full transition-all duration-300 hover:shadow-lg"
                        data-testid={`book-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        Book This Service
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      <section 
        className="section-padding bg-cover bg-center bg-fixed relative"
        style={{
          backgroundImage: 'linear-gradient(135deg, hsl(var(--spiritual) / 0.94) 0%, hsl(var(--compassion) / 0.84) 100%)'
        }}
        data-testid="testimonials-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title font-heading font-bold text-4xl md:text-5xl text-white mb-6">
              What Our Clients Say
            </h2>
            <p className="text-xl text-compassionScale-100 max-w-3xl mx-auto leading-relaxed">
              Real stories of transformation and healing from our community.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={index} 
                className="bg-white/95 backdrop-blur-sm border-0 hover:bg-white transition-all duration-300 hover:scale-105"
                data-testid={`testimonial-${index}`}
              >
                <CardContent className="p-8 text-center">
                  <div className="flex justify-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  
                  <blockquote className="text-compassion-foreground mb-6 leading-relaxed italic">
                    "{testimonial.text}"
                  </blockquote>
                  
                  <div className="font-semibold text-spiritual">
                    {testimonial.name}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="section-padding bg-gradient-to-r from-spiritual via-compassion to-spiritual" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-6">
            Ready to Begin Your Healing Journey?
          </h2>
          
          <p className="text-xl text-compassionScale-100 mb-8 max-w-2xl mx-auto leading-relaxed">
            Take the first step towards balance, peace, and spiritual wellness. 
            Your transformation awaits.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/contact">
              <Button 
                size="lg" 
                  className="bg-white text-spiritual hover:bg-sacredScale-50 px-8 py-4 text-lg font-semibold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
                data-testid="contact-us-btn"
              >
                <Phone className="mr-2 w-5 h-5" />
                Contact Us
              </Button>
            </Link>
            
            <a href="mailto:healingatmysticprana@gmail.com">
              <Button 
                variant="outline" 
                size="lg" 
                  className="text-white border-white hover:bg-white hover:text-spiritual px-8 py-4 text-lg font-semibold rounded-full transition-all duration-300 hover:scale-105"
                data-testid="email-us-btn"
              >
                <Mail className="mr-2 w-5 h-5" />
                Email Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
